/**
 * BookPage — /book/{series_key}. One multi-chapter series presented as
 * an audiobook: cover, ordered chapters with inline audio players,
 * links to the full story pages, and the dedicated podcast RSS feed.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Film, Loader2, BookOpen, Copy, Check, Rss, ArrowRight, Headphones } from "lucide-react";
import { useTranslation } from "@/i18n";
import LanguagePicker from "@/LanguagePicker";
import { mediaUrl } from "@/mediaUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDuration(secs) {
  if (!secs) return null;
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

export default function BookPage({ seriesKey }) {
  const [t] = useTranslation();
  const [book, setBook] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/books/${encodeURIComponent(seriesKey)}`)
      .then(({ data }) => setBook(data))
      .catch(() => setError("This book could not be found."));
  }, [seriesKey]);

  const copyRss = async () => {
    try {
      await navigator.clipboard.writeText(book.rss_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <p className="font-display text-xl">{error}</p>
      </div>
    );
  }
  if (!book) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[color:var(--accent)]" />
      </div>
    );
  }

  const totalLabel = book.total_duration_seconds > 0
    ? `${Math.floor(book.total_duration_seconds / 3600) ? `${Math.floor(book.total_duration_seconds / 3600)}h ` : ""}${Math.round((book.total_duration_seconds % 3600) / 60)}m`
    : null;

  return (
    <div className="min-h-screen" data-testid="book-page">
      <header className="px-6 md:px-10 py-6 md:py-8 border-b border-white/5">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src="/brand-s.png" alt="Storylo" className="w-9 h-9 rounded-full" data-testid="brand-logo" />
            <div>
              <div className="font-display text-lg leading-none">Storylo</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--muted)] mt-1">
                {t("tagline")}
              </div>
            </div>
          </a>
          <nav className="flex items-center gap-3 sm:gap-4 text-sm">
            <a href="/" className="text-[color:var(--muted)] hover:text-white transition-colors">{t("nav_all")}</a>
            <a href="/books" className="text-white">{t("nav_books")}</a>
            <LanguagePicker />
          </nav>
        </div>
      </header>

      <main className="px-6 md:px-10 py-12 md:py-16">
        <div className="max-w-[900px] mx-auto">
          <div className="md:flex gap-8 items-start">
            {book.cover_url && (
              <div className="w-full md:w-72 shrink-0 rounded-xl overflow-hidden panel mb-6 md:mb-0">
                <img src={mediaUrl(book.cover_url)} alt={book.title} className="w-full object-cover" />
              </div>
            )}
            <div className="flex-1">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--accent)] mb-3 flex items-center gap-2">
                <BookOpen className="w-3.5 h-3.5" /> Audiobook
              </div>
              <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">
                {book.title}
              </h1>
              <div className="text-sm text-[color:var(--muted)] mt-3">
                {book.chapter_count} {t("book_chapters")}
                {totalLabel && ` · ${totalLabel} ${t("book_listen_total")}`}
              </div>

              <div className="mt-6 panel p-4 border-[color:var(--accent)]/25">
                <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--accent)] mb-2 flex items-center gap-1.5">
                  <Rss className="w-3 h-3" /> {t("book_subscribe")}
                </div>
                <div className="text-xs text-[color:var(--muted)] leading-relaxed mb-3">
                  {t("book_subscribe_hint")}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <code className="text-[11px] bg-black/30 border border-white/10 rounded-md px-2.5 py-1.5 break-all flex-1 min-w-0">
                    {book.rss_url}
                  </code>
                  <button
                    onClick={copyRss}
                    className={`text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-md border inline-flex items-center gap-1.5 transition-colors shrink-0 ${
                      copied
                        ? "border-emerald-500/50 text-emerald-400"
                        : "border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"
                    }`}
                    data-testid="book-copy-rss"
                  >
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 space-y-3" data-testid="book-chapters">
            {book.chapters.map((c) => (
              <div key={c.id} className="panel p-4 md:p-5" data-testid={`book-chapter-${c.chapter}`}>
                <div className="flex items-start gap-4">
                  <div className="w-10 shrink-0 font-display text-[color:var(--accent)] text-2xl text-center leading-none pt-0.5">
                    {String(c.chapter || 0).padStart(2, "0")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg leading-snug">{c.title}</div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] mt-1 flex items-center gap-2">
                      <Headphones className="w-3 h-3" />
                      {fmtDuration(c.duration_seconds) || "—"}
                    </div>
                    <audio
                      controls
                      preload="none"
                      src={mediaUrl(c.audio_url)}
                      className="mt-3 w-full h-9"
                      data-testid={`book-audio-${c.chapter}`}
                    />
                    {c.summary && (
                      <p className="text-xs text-[color:var(--muted)] mt-2 leading-relaxed line-clamp-2">{c.summary}</p>
                    )}
                  </div>
                  <a
                    href={`/s/${c.slug}`}
                    className="shrink-0 text-[10px] uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-md border border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] inline-flex items-center gap-1"
                    data-testid={`book-watch-${c.chapter}`}
                  >
                    {t("book_watch")} <ArrowRight className="w-3 h-3" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
